import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import { palette } from 'constants';
import { Spinner } from '..';

const borderedPanelClass =
  'overflow-hidden rounded-[10px] border border-legacy-hairline font-inter';

const tableClassName = 'box-border w-full text-left font-inter';

const thClassName =
  "border-b border-legacy-hairline !font-['InterMedium',sans-serif] !text-[14px] font-medium !text-legacy-textBody";

const tdBaseStyle = {
  padding: '12px 16px',
  backgroundColor: 'transparent',
  borderBottom: `1px solid ${palette.borderLight}`,
};

const tdWidePaddingStyle = {
  padding: '24px 16px',
  backgroundColor: 'transparent',
  borderBottom: `1px solid ${palette.borderLight}`,
};

const tdTextClass =
  '!font-inter !text-[14px] !font-normal !text-legacy-textDark';

const tableElementStyle = {
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
  boxSizing: 'border-box',
};

function Table({
  className,
  wrapperClassName,
  columns = [],
  data,
  loading,
  dataRowId = (element) => element.id,
  titleNoWrap,
  payment = false,
  emptyMessage,
  maxHeight,
  disableSort = false,
  loadingSpinnerId = 'table-loading-spinner',
}) {
  const colCount = columns.length || 1;

  const sortKey = columns[0]?.key;
  let sortedData = [...(data || [])];
  if (!disableSort && sortKey) {
    sortedData.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av < bv) return 1;
      if (av > bv) return -1;
      return 0;
    });
  }

  const resolvedEmptyMessage =
    emptyMessage ?? (payment ? 'No payment history.' : 'No policy documents.');

  const hasScrollableBody = maxHeight != null;

  const renderBodyRows = () => {
    if (loading) {
      return (
        <tr
          className={hasScrollableBody ? 'table w-full table-fixed' : undefined}
        >
          <td
            colSpan={colCount}
            style={tdWidePaddingStyle}
            className="text-center"
          >
            <div className="flex justify-center">
              <Spinner id={loadingSpinnerId} size={80} />
            </div>
          </td>
        </tr>
      );
    }

    if (data && data.length !== 0) {
      return sortedData.map((row, i) => (
        <tr
          data-testid={`row-${i}`}
          key={dataRowId(row) || i}
          className={hasScrollableBody ? 'table w-full table-fixed' : undefined}
        >
          {columns.map((c) => (
            <td
              key={c.key}
              style={{ ...tdBaseStyle, ...c.cellStyle }}
              className={clsx(tdTextClass, c.cellClassName)}
              data-testid={`${c.key}-${i}`}
            >
              {c.render ? c.render(row) : row[c.key]}
            </td>
          ))}
        </tr>
      ));
    }

    return (
      <tr
        className={hasScrollableBody ? 'table w-full table-fixed' : undefined}
      >
        <td
          colSpan={colCount}
          style={tdWidePaddingStyle}
          className={clsx('text-center', tdTextClass)}
        >
          {resolvedEmptyMessage}
        </td>
      </tr>
    );
  };

  return (
    <div>
      <div className={clsx(borderedPanelClass, wrapperClassName)}>
        <div
          className="font-inter overflow-x-auto overflow-y-auto"
          style={
            !hasScrollableBody && maxHeight != null ? { maxHeight } : undefined
          }
        >
          <table
            className={clsx(tableClassName, className)}
            style={{
              ...tableElementStyle,
              ...(hasScrollableBody ? { tableLayout: 'fixed' } : {}),
            }}
          >
            <thead className={hasScrollableBody ? 'block w-full' : undefined}>
              <tr
                className={clsx(
                  '!bg-legacy-bgLight w-full',
                  hasScrollableBody && 'table table-fixed',
                )}
              >
                {columns.map((c) => (
                  <th
                    key={c.key}
                    scope="col"
                    style={{ padding: '12px 16px', ...c.headerStyle }}
                    className={clsx(
                      thClassName,
                      titleNoWrap && 'whitespace-nowrap',
                      c.headerClassName,
                    )}
                  >
                    {c.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody
              className={clsx(
                'bg-legacy-dashboardBg',
                hasScrollableBody && 'block w-full',
              )}
              style={hasScrollableBody ? { maxHeight } : undefined}
            >
              {renderBodyRows()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

Table.propTypes = {
  loading: PropTypes.bool,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.node.isRequired,
      render: PropTypes.func,
      headerStyle: PropTypes.object,
      headerClassName: PropTypes.string,
      cellStyle: PropTypes.object,
      cellClassName: PropTypes.string,
    }),
  ),
  data: PropTypes.array,
  className: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  wrapperClassName: PropTypes.string,
  dataRowId: PropTypes.func,
  titleNoWrap: PropTypes.bool,
  payment: PropTypes.bool,
  emptyMessage: PropTypes.string,
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disableSort: PropTypes.bool,
  loadingSpinnerId: PropTypes.string,
};

export default Table;
